<template>
    <div class="d-flex flex-row w-100">
        <div class="leftcolumn mr-4">
            <div v-if="!jobLoading">
                <Paper class="p-4 mb-4">
                    <div class="jobtitle">{{ job.title }}</div>

                    <div class="d-flex align-items-align-items-start">
                        <div v-if="job.location" class="d-flex align-items-align-items-start">
                            <BaseIcon name="map-marker-alt" class="icon gray-color" />
                            <span class="subtitle my-2 mr-3">{{ job.location }}</span>
                        </div>

                        <div v-if="job.deadline" class="d-flex align-items-align-items-start">
                            <BaseIcon name="clock" class="icon gray-color" />
                            <span class="subtitle my-2">{{ formattedDeadline }}</span>
                        </div>
                    </div>
                </Paper>

                <div v-if="true">
                    <Paper v-if="chatUsers().length && showChatUsers" header="Chat" class="mb-4">
                        <Carousel
                            :items="chatUsers()"
                            :component="getCarouselComponent()"
                            tightCols
                            dontCenter
                            @item-click="handleChatUserClick"
                        />
                    </Paper>
                </div>

                <Paper v-if="job.description" class="p-4 mb-4">
                    <div v-html="job.description"></div>
                </Paper>
            </div>
        </div>

        <div class="rightcolumn flex-grow-1">
            <CompanyBadge class="mb-4" :company="company" :routerLink="boothLink" />

            <DetailModule
                class="mb-4"
                :data="job"
                :isVerified="true"
                :isPending="false"
                :isLoading="jobLoading"
                :vFairId="$route.params.id"
                :pavilionId="pavilionId"
            />
        </div>
    </div>
</template>

<script>
import moment from 'moment';
import Paper from '~/components/builders/Paper';
import CompanyBadge from './components/CompanyBadge';
import DetailModule from '~/views/jobdetail/components/DetailModule';
import AnalyticService from '~/services/AnalyticService';
import { SHOW_CHAT_IN_COMPANY_PROFILE } from '~/router';
import AuthService from '~/services/AuthService';
import RestX from 'highered-utils/restx';
import ChatUserCarouselItem from '../../companydetail/components/ChatUser.CarouselItem';

export default {
    name: 'Job',
    data() {
        return {
            loading: false,
            hasLoggedEvent: false,
            showChatUsers: SHOW_CHAT_IN_COMPANY_PROFILE,
            now: 0,
            chatAvailabilityIntervalSeconds: 5,
            chatAvailabilityInterval: null,
        };
    },
    components: {
        Paper,
        CompanyBadge,
        DetailModule,
    },
    computed: {
        postingRequest() {
            if (!this.$route.params.jobId) {
                return undefined;
            } else {
                return this.$interfaces.posting.id(this.$route.params.jobId).get({
                    reactive: true,
                    isJobAdId: true,
                });
            }
        },
        job() {
            if (!this.postingRequest || this.postingRequest.loading) {
                return null;
            } else {
                return this.postingRequest.data;
            }
        },
        jobLoading() {
            return !this.postingRequest || this.postingRequest.loading;
        },
        formattedDeadline() {
            const deadline = moment(this.job.deadline);
            return deadline.format('DD MMM YYYY');
        },
        boothLink() {
            if (this.job) {
                let url = this.$URLS.vfair
                    .concat('/', this.$route.params.id)
                    .concat('/booths/', this.job.companyId);

                this.$route.query.pavilion_id &&
                    (url += `?pavilion_id=${this.$route.query.pavilion_id}`);

                return url;
            } else {
                return '';
            }
        },
        company() {
            if (this.job) {
                var request = this.$interfaces.vfair
                    .id(this.$route.params.id)
                    .organizations.organizationId(this.job.companyId)
                    .get({ reactive: true });
                if (request.loading) {
                    return {};
                } else {
                    return request.data;
                }
            } else {
                return {};
            }
        },
        pavilionId() {
            return this.$interfaces.vfairModule.getPavilionId();
        },
        availableChatUsers() {
            return SHOW_CHAT_IN_COMPANY_PROFILE
                ? this.$interfaces.communication.chat.availability.id
                      .org_id(this.job.companyId)
                      .get({ reactive: true })
                : [];
        },
    },
    methods: {
        chatUsers() {
            if (!this.availableChatUsers.data) return [];
            return this.availableChatUsers.data
                .filter(cu => cu.availableUntil > this.now)
                .map(item => ({
                    ...item,
                    companyId: parseInt(this.job.companyId),
                    companyTitle: this.job.companyName,
                }));
        },
        chatUsersLoading() {
            return SHOW_CHAT_IN_COMPANY_PROFILE ? this.availableChatUsers.loading : false;
        },
        handleChatUserClick(item) {
            if (!AuthService.isLoggedIn()) {
                RestX.getInterfaces().logInModule.toggleOpen();
            }
            this.$root.$emit('chat-conversation-create-update', item.id);
        },
        chatAvailabilityTick() {
            this.now += this.chatAvailabilityIntervalSeconds;
        },
        getCarouselComponent() {
            return ChatUserCarouselItem;
        },
        logJobadViewEvent() {
            // This triggers an Google Analytics event
            const data = this.job;
            if (data.jobAdId) {
                AnalyticService.jobadViewEvent(
                    data.jobAdId,
                    data.companyId,
                    data.title,
                    this.$route.params.id,
                    this.pavilionId
                );
                this.hasLoggedEvent = true;
            }
        },
    },
    watch: {
        job: {
            immediate: true,
            handler(newPosting, oldPosting) {
                if (
                    newPosting &&
                    (!this.hasLoggedEvent || newPosting.jobAdId !== oldPosting.jobAdId)
                ) {
                    this.logJobadViewEvent();
                }
            },
        },
    },
    mounted() {
        if (SHOW_CHAT_IN_COMPANY_PROFILE) {
            this.now = Math.round(new Date().getTime() / 1000);
            this.chatAvailabilityInterval = setInterval(
                this.chatAvailabilityTick,
                this.chatAvailabilityIntervalSeconds * 1000
            );
        }
    },
    beforeDestroy() {
        if (SHOW_CHAT_IN_COMPANY_PROFILE) {
            clearInterval(this.chatAvailabilityInterval);
            this.chatAvailabilityInterval = null;
        }
    },
};
</script>

<style scoped>
.leftcolumn {
    width: 65%;
    margin-right: 22px;
}
.jobtitle {
    font-size: 18px;
}
.companytitle {
    font-size: 18px;
    text-align: center;
}
.logo {
    width: 180px;
    height: 70px;
    object-fit: contain;
}
</style>
